import React, { useState, useEffect, useRef } from 'react';
import { ShieldCheck, Info, X } from 'lucide-react';
import { cn } from '../lib/utils';
// eslint-disable-next-line no-unused-vars
import { motion, AnimatePresence } from 'framer-motion';

export default function ContentCredentialBadge({ children, className, style, did }) {
  const [contextMenu, setContextMenu] = useState({ isOpen: false, x: 0, y: 0 });
  const containerRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (contextMenu.isOpen && containerRef.current && !containerRef.current.contains(e.target)) {
        setContextMenu(prev => ({ ...prev, isOpen: false }));
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('scroll', () => setContextMenu(prev => prev.isOpen ? { ...prev, isOpen: false } : prev), { passive: true });
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('scroll', () => {});
    };
  }, [contextMenu.isOpen, setContextMenu]);

  const handleContextMenu = (e) => {
    e.preventDefault();
    setContextMenu({ isOpen: true, x: e.clientX, y: e.clientY });
  };

  const handleDragStart = (e) => {
    e.preventDefault();
    setContextMenu({ isOpen: true, x: e.clientX, y: e.clientY });
  };



  return (
    <>
      <div 
        className={cn('relative inline-block cursor-context-menu', className)} 
        style={style}
        onContextMenu={handleContextMenu}
        onDragStart={handleDragStart}
      >
        {children}
      </div>

      <AnimatePresence>
        {contextMenu.isOpen && (
          <motion.div
            ref={containerRef}
            initial={{ opacity: 0, scale: 0.95, rotateX: 10 }}
            animate={{ opacity: 1, scale: 1, rotateX: 0 }}
            exit={{ opacity: 0, scale: 0.95, rotateX: -10 }}
            transition={{ duration: 0.2, type: 'spring', stiffness: 300, damping: 25 }}
            style={{ 
              position: 'fixed', 
              left: Math.min(contextMenu.x, window.innerWidth - 300), 
              top: Math.min(contextMenu.y, window.innerHeight - 300),
              zIndex: 99999,
              perspective: '1000px'
            }}
            className="w-72 bg-slate-900/90 backdrop-blur-3xl border border-white/20 rounded-2xl shadow-[0_32px_64px_rgba(0,0,0,0.5)] overflow-hidden"
          >
            <div className="flex items-center justify-between p-3 border-b border-white/10 bg-black/20">
              <div className="flex items-center gap-2 text-white/90">
                <ShieldCheck className="w-4 h-4 text-emerald-400 drop-shadow-[0_0_8px_rgba(52,211,153,0.5)]" />
                <span className="text-[11px] font-black uppercase tracking-widest">Protected Media</span>
              </div>
              <button 
                onClick={() => setContextMenu({ ...contextMenu, isOpen: false })}
                className="p-1 hover:bg-white/10 rounded-full transition-colors"
              >
                <X className="w-4 h-4 text-white/50 hover:text-white" />
              </button>
            </div>

            <div className="p-4">
              <div className="flex items-start gap-2 mb-4">
                <Info className="w-4 h-4 text-white/60 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-[11px] text-white/80 leading-relaxed font-medium">
                    This media is cryptographically signed. Any modifications will invalidate the C2PA signature.
                  </p>
                </div>
              </div>

              <div className="bg-black/40 p-3 rounded-lg border border-white/10 mb-4 shadow-inner">
                <p className="text-[10px] text-white/50 font-mono break-all leading-relaxed">
                  <span className="text-white/30 uppercase text-[8px] tracking-widest block mb-1">Issuer</span>
                  Linkit Sovereign Vault
                  <span className="text-white/30 uppercase text-[8px] tracking-widest block mt-2 mb-1">DID (Decentralized ID)</span>
                  {did || 'Unknown'}
                  <span className="text-white/30 uppercase text-[8px] tracking-widest block mt-2 mb-1">Status</span>
                  <span className="text-emerald-400 font-bold drop-shadow-sm">Cryptographically Verified</span>
                </p>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
